import React, { useEffect, useMemo, useRef, useState } from 'react';
import ActionButton from 'components/ActionButton/ActionButton';
import PriceFilter from 'components/PriceFilter/PriceFilter';
import SetFilter from 'components/SetFilter/SetFilter';
import QtyControl from 'components/QtyControl/QtyControl';
import SegmentedToggle from 'components/SegmentedToggle';
import MobileSheet from 'components/MobileSheet';
import { AssetUrls } from 'config/assets';
import { mapBackendToFrontend } from 'constants/languages';
// The magenta dismissible-token treatment, reused as-is rather than recreated.
// Imported explicitly so this header does not silently depend on /search having
// been bundled first.
import 'screens/Main/Search/Components/FilterTokenRow.css';
import SelectionMenu from './components/SelectionMenu';
import FiltersPopover from './components/FiltersPopover';
import {
  buildTokens,
  countAppliedFilters,
  PrintingsFilterState,
} from './components/printingsFilters';
import './CardPrintingsHeader.css';

interface SetOption {
  code: string;
  name: string;
  count: number;
}

interface CardPrintingsHeaderProps {
  selectedCount: number;
  onAddToWantlist: () => void;
  onPriceChange?: (min: number, max: number) => void;
  onGroupChange?: (value: string) => void;
  onQtyChange?: (value: number) => void;
  minPrice?: number;
  maxPrice?: number;
  isAddingToWantlist?: boolean;
  buttonText?: string;
  headerTitle?: string;
  /**
   * Retained for the edit-mode string, but no longer rendered: dropping the
   * long explanatory paragraph is what pays for the selection control without
   * the header growing.
   */
  headerSubtitle?: string;
  defaultQuantity?: number;
  initialFoil?: boolean;
  initialRegular?: boolean;
  initialEtched?: boolean;
  /** Finish chip labels to render disabled (no printing of this card has them) */
  disabledFinishes?: string[];
  initialLanguages?: string[];
  initialConditions?: string[];
  onFiltersChange?: (filters: PrintingsFilterState) => void;
  // Set filter props
  sets?: SetOption[];
  selectedSet?: string | null;
  onSetChange?: (setCode: string | null) => void;
  // View mode props
  viewMode?: 'image' | 'list';
  onViewModeChange?: (mode: 'image' | 'list') => void;
  // Page-level selection
  totalCount?: number;
  groupBy?: string;
  onSelectAll?: () => void;
  onRemoveAll?: () => void;
  onInvertSelection?: () => void;
}

const GROUP_OPTIONS = [
  { value: 'Artist', label: 'Artist' },
  { value: 'Border', label: 'Border' },
];

const VIEW_OPTIONS = [
  { value: 'image' as const, label: 'Image' },
  { value: 'list' as const, label: 'List' },
];

const CardPrintingsHeader: React.FC<CardPrintingsHeaderProps> = ({
  selectedCount,
  onAddToWantlist,
  onPriceChange,
  onGroupChange,
  onQtyChange,
  minPrice,
  maxPrice,
  isAddingToWantlist = false,
  buttonText,
  headerTitle,
  defaultQuantity,
  initialFoil,
  initialRegular,
  initialEtched,
  disabledFinishes,
  initialLanguages,
  initialConditions,
  onFiltersChange,
  sets,
  selectedSet,
  onSetChange,
  viewMode,
  onViewModeChange,
  totalCount = 0,
  groupBy = 'Artist',
  onSelectAll,
  onRemoveAll,
  onInvertSelection,
}) => {
  /**
   * The values the page loaded with. In edit mode these are the user's saved
   * want-list preferences, which is why "Clear all" restores THESE rather than
   * emptying the filters -- clearing to empty would silently discard what the
   * user already saved.
   *
   * Captured from the first render that has real data: the props arrive
   * asynchronously in edit mode, so freezing the pre-load empty state would
   * make the baseline wrong for exactly the users who have preferences.
   */
  const defaultsRef = useRef<PrintingsFilterState | null>(null);
  const loadDefaults: PrintingsFilterState = useMemo(
    () => ({
      foil: [...(initialRegular ? ['Non-foil'] : []), ...(initialFoil ? ['Foil'] : []), ...(initialEtched ? ['Etched'] : [])],
      // Normalized to frontend uppercase codes: the page sends 'EN' from saved
      // want-list defaults but backend 'en'/'zh-hans' in edit mode, and the
      // popover chips compare against codes. Unnormalized values could never
      // match a chip, so a click appended instead of toggling.
      languages: mapBackendToFrontend(initialLanguages ?? []),
      conditions: initialConditions ?? [],
    }),
    [initialFoil, initialRegular, initialEtched, initialLanguages, initialConditions],
  );
  if (
    defaultsRef.current === null &&
    (loadDefaults.foil.length > 0 ||
      loadDefaults.languages.length > 0 ||
      loadDefaults.conditions.length > 0)
  ) {
    defaultsRef.current = loadDefaults;
  }
  const defaults = defaultsRef.current ?? loadDefaults;

  const [filters, setFilters] = useState<PrintingsFilterState>(loadDefaults);
  const hasUserEdited = useRef(false);

  // Until the user touches a filter, follow the props: in edit mode the saved
  // entry lands after mount and must be reflected, not overwritten.
  useEffect(() => {
    if (!hasUserEdited.current) setFilters(loadDefaults);
  }, [loadDefaults]);

  const applyFilters = (next: PrintingsFilterState) => {
    hasUserEdited.current = true;
    setFilters(next);
    onFiltersChange?.(next);
  };

  const patchFilters = (patch: Partial<PrintingsFilterState>) =>
    applyFilters({ ...filters, ...patch });

  const tokens = useMemo(() => buildTokens(filters, defaults), [filters, defaults]);
  const appliedCount = useMemo(() => countAppliedFilters(filters, defaults), [filters, defaults]);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFiltersOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setIsFiltersOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsFiltersOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isFiltersOpen]);

  const clearToken = (dimension: keyof PrintingsFilterState, value: string) =>
    patchFilters({
      [dimension]: filters[dimension].filter((v) => v !== value),
    } as Partial<PrintingsFilterState>);

  const clearAll = () => applyFilters(defaults);

  const filtersBadge = appliedCount > 0 && <span className="cp-filters-badge">{appliedCount}</span>;

  return (
    <div className="card-printings-header">
      <div className="header-top">
        <div className="header-text">
          <h2 className="title">{headerTitle || 'Choose desired versions'}</h2>
          {/* The long explanatory subtitle is gone: the title plus this live
              line carries the meaning, and its removal is what pays for the
              selection control without the header getting taller. */}
          <div className="cp-title-meta">
            <span className="cp-total">{totalCount} printings</span>
            <SelectionMenu
              totalCount={totalCount}
              selectedCount={selectedCount}
              onSelectAll={onSelectAll || (() => {})}
              onRemoveAll={onRemoveAll || (() => {})}
              onInvert={onInvertSelection || (() => {})}
            />
          </div>
        </div>
        {/* .header-action .action-button and the <span>-wrapped label are asserted
            by CardPrintingsHeader.translate.test.tsx -- they guard Sentry
            JAVASCRIPT-REACT-13, where browser translate re-parents bare text
            nodes and React's commit-phase deletion then throws. Keep both. */}
        <div className="header-action">
          <QtyControl defaultValue={defaultQuantity || 1} onChange={onQtyChange} />
          <ActionButton
            onPress={onAddToWantlist}
            isLoading={isAddingToWantlist}
            isDisabled={selectedCount === 0}
          >
            <img
              src={AssetUrls.icons.misc.heart}
              alt="Heart Icon"
              style={{ width: '14px', height: '14px', marginRight: '6px', flexShrink: 0 }}
            />
            <span>{isAddingToWantlist ? (buttonText?.includes('Update') ? 'Updating...' : 'Adding...') : (buttonText || 'Add to Wantlist')}</span>
          </ActionButton>
        </div>
      </div>

      <div className="header-controls">
        <div className="controls-row">
          <SetFilter
            sets={sets || []}
            selectedSet={selectedSet ?? null}
            onChange={onSetChange}
          />

          {/* The 'points' variant: one coin-labelled field, never "Price" —
              the unit on this site is points. */}
          <PriceFilter
            variant="points"
            defaultMin={minPrice}
            defaultMax={maxPrice}
            onChange={onPriceChange}
          />

          <div className="cp-field cp-segment-field">
            <span className="cp-field-label">Group</span>
            <SegmentedToggle
              options={GROUP_OPTIONS}
              value={groupBy}
              onChange={(value) => onGroupChange?.(value)}
              ariaLabel="Group printings by"
            />
          </div>

          {/* View mode is a desktop refinement: on a 390pt screen there is no
              room for a fourth control, so mobile defaults to Image. */}
          {onViewModeChange && (
            <div className="cp-field cp-segment-field cp-desktop-only">
              <span className="cp-field-label">View</span>
              <SegmentedToggle
                options={VIEW_OPTIONS}
                value={viewMode || 'image'}
                onChange={onViewModeChange}
                ariaLabel="View mode"
              />
            </div>
          )}

          <div className="cp-filters cp-desktop-only" ref={filtersRef}>
            <button
              type="button"
              className={`cp-filters-btn ${isFiltersOpen ? 'open' : ''}`}
              onClick={() => setIsFiltersOpen((v) => !v)}
              aria-expanded={isFiltersOpen}
            >
              Filters
              {filtersBadge}
            </button>
            {isFiltersOpen && (
              <div className="cp-filters-popover">
                <FiltersPopover
                  state={filters}
                  onChange={patchFilters}
                  onReset={clearAll}
                  onDone={() => setIsFiltersOpen(false)}
                  disabledFinishes={disabledFinishes}
                />
              </div>
            )}
          </div>

          {/* Mobile opens the same body as a bottom sheet instead of a popover. */}
          <button
            type="button"
            className="cp-filters-btn cp-mobile-only"
            onClick={() => setIsSheetOpen(true)}
            aria-expanded={isSheetOpen}
          >
            Filters
            {filtersBadge}
          </button>
        </div>

        {tokens.length > 0 && (
          <div className="cp-token-row filter-token-row scrollable">
            {tokens.map((token) => (
              <button
                key={token.id}
                type="button"
                className="filter-token"
                onClick={() => clearToken(token.dimension, token.value)}
              >
                {token.label}
                <span className="filter-token-close" aria-hidden="true">
                  &times;
                </span>
              </button>
            ))}
            <button type="button" className="filter-token-clear-all" onClick={clearAll}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {isSheetOpen && (
        <MobileSheet
          ariaLabel="Filters"
          onClose={() => setIsSheetOpen(false)}
          applyLabel={`Show ${totalCount} version${totalCount === 1 ? '' : 's'}`}
        >
          <FiltersPopover
            state={filters}
            onChange={patchFilters}
            onReset={clearAll}
            onDone={() => setIsSheetOpen(false)}
            disabledFinishes={disabledFinishes}
            touch
          />
        </MobileSheet>
      )}
    </div>
  );
};

export default CardPrintingsHeader;
